import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { apiFetch, parseJsonSafe } from '../../utils/api';

const initialState = {
  razonSocial: '',
  cif: '',
  direccion: '',
  codigoPostal: '',
  ciudad: '',
  provincia: '',
  pais: '',
  telefono: '',
  email: '',
  tipo: 'Cliente',
  estado: 'Activo',
  dolibarrId: '',
  canalCaptacion: 'Sin establecer',
  utmMedium: '',
  utmCampaign: '',
  leadPipeline: false
};

const ClientFormPage = ({ mode }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [createdId, setCreatedId] = useState(null);
  const [error, setError] = useState('');
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (mode === 'edit' && id) {
      apiFetch(`/api/clients/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(async (res) => {
          const data = await parseJsonSafe(res);
          if (!res.ok) throw new Error(data.message || 'Error cargando cliente');
          setForm({
            razonSocial: data.razon_social || '',
            cif: data.cif || '',
            direccion: data.direccion || '',
            codigoPostal: data.codigo_postal || '',
            ciudad: data.ciudad || '',
            provincia: data.provincia || '',
            pais: data.pais || '',
            telefono: data.telefono || '',
            email: data.email || '',
            tipo: data.tipo || 'Cliente',
            estado: data.estado || 'Activo',
            dolibarrId: data.dolibarr_id || '',
            canalCaptacion: data.canal_captacion || 'Sin establecer',
            utmMedium: data.utm_medium || '',
            utmCampaign: data.utm_campaign || '',
            leadPipeline: Boolean(data.lead_pipeline)
          });
        })
        .catch((err) => setError(err.message));
    }
  }, [id, mode, token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'tipo') {
        if (value === 'Lead') {
          next.leadPipeline = true;
        } else {
          next.leadPipeline = false;
        }
      }
      if (name === 'canalCaptacion' && value !== 'Webhook') {
        next.utmMedium = '';
        next.utmCampaign = '';
      }
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const dolibarrIdTrim = String(form.dolibarrId || '').trim();
      if (dolibarrIdTrim && !/^[0-9]+$/.test(dolibarrIdTrim)) {
        throw new Error('ID Dolibarr inválido (solo números)');
      }
      const payload = { ...form, dolibarrId: dolibarrIdTrim || null };
      const res = await apiFetch(mode === 'edit' ? `/api/clients/${id}` : '/api/clients', {
        method: mode === 'edit' ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'Error guardando cliente');
      if (mode === 'edit') navigate(`/clientes/${id}`);
      else setCreatedId(data.id || data.client?.id || data.data?.id);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card shadow-sm">
      <div className="card-body">
        {createdId ? <div className="alert alert-success" role="status">Cliente creado. <Link className="btn btn-sm btn-success ms-2" to={`/clientes/${createdId}`}>Abrir cliente</Link><Link className="btn btn-sm btn-outline-secondary ms-2" to="/clientes">Volver a clientes</Link><button type="button" className="btn btn-sm btn-link ms-2" onClick={() => { setCreatedId(null); setForm(initialState); }}>Crear otro cliente</button></div> : null}
        <h3>{mode === 'edit' ? 'Editar cliente' : 'Crear cliente'}</h3>
        {error && <div className="alert alert-danger">{error}</div>}
        <form onSubmit={handleSubmit} className="row g-3" hidden={Boolean(createdId)}>
          <div className="col-md-6">
            <label className="form-label">Razón Social *</label>
            <input name="razonSocial" value={form.razonSocial} onChange={handleChange} className="form-control" required />
          </div>
          <div className="col-md-6">
            <label className="form-label">CIF/NIF</label>
            <input name="cif" value={form.cif} onChange={handleChange} className="form-control" />
          </div>
          <div className="col-md-8">
            <label className="form-label">Dirección</label>
            <input name="direccion" value={form.direccion} onChange={handleChange} className="form-control" />
          </div>
          <div className="col-md-4">
            <label className="form-label">Código Postal</label>
            <input name="codigoPostal" value={form.codigoPostal} onChange={handleChange} className="form-control" />
          </div>
          <div className="col-md-4">
            <label className="form-label">Ciudad</label>
            <input name="ciudad" value={form.ciudad} onChange={handleChange} className="form-control" />
          </div>
          <div className="col-md-4">
            <label className="form-label">Provincia</label>
            <input name="provincia" value={form.provincia} onChange={handleChange} className="form-control" />
          </div>
          <div className="col-md-4">
            <label className="form-label">País</label>
            <input name="pais" value={form.pais} onChange={handleChange} className="form-control" />
          </div>
          <div className="col-md-6">
            <label className="form-label">Teléfono</label>
            <input name="telefono" value={form.telefono} onChange={handleChange} className="form-control" />
          </div>
          <div className="col-md-6">
            <label className="form-label">Email</label>
            <input name="email" type="email" value={form.email} onChange={handleChange} className="form-control" />
          </div>
          <div className="col-md-6">
            <label className="form-label">Tipo</label>
            <select name="tipo" value={form.tipo} onChange={handleChange} className="form-select">
              <option value="Cliente">Cliente</option>
              <option value="Lead">Lead</option>
            </select>
          </div>
          <div className="col-md-6">
            <label className="form-label">Estado</label>
            <select name="estado" value={form.estado} onChange={handleChange} className="form-select">
              <option value="Activo">Activo</option>
              <option value="Inactivo">Inactivo</option>
            </select>
          </div>
          <div className="col-md-6">
            <label className="form-label">ID Dolibarr</label>
            <input
              name="dolibarrId"
              value={form.dolibarrId}
              onChange={handleChange}
              className="form-control"
              inputMode="numeric"
              placeholder="Ej: 123"
            />
            <div className="form-text">
              Si ya existe el tercero en Dolibarr, pon aquí su ID para asociarlo.
            </div>
          </div>
          {form.tipo === 'Lead' && (
            <div className="col-md-6">
              <label className="form-label">Embudo de ventas</label>
              <div className="form-check form-switch mt-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="leadPipeline"
                  checked={form.leadPipeline}
                  onChange={(e) => setForm((prev) => ({ ...prev, leadPipeline: e.target.checked }))}
                />
                <label className="form-check-label" htmlFor="leadPipeline">
                  Incluir en el kanban
                </label>
              </div>
            </div>
          )}
          <div className="col-12">
            <h6 className="mt-2 mb-0">Canal captación</h6>
          </div>
          <div className="col-md-6">
            <label className="form-label">Canal</label>
            <select name="canalCaptacion" value={form.canalCaptacion} onChange={handleChange} className="form-select">
              <option value="Sin establecer">Sin establecer</option>
              <option value="Webhook">Webhook</option>
              <option value="Whatsapp">Whatsapp</option>
              <option value="Email">Email</option>
              <option value="Llamada">Llamada</option>
              <option value="Google Ads">Google Ads</option>
              <option value="Email marketing">Email marketing</option>
              <option value="BORME">BORME</option>
              <option value="Otros">Otros</option>
            </select>
          </div>
          {form.canalCaptacion === 'Webhook' && (
            <>
              <div className="col-md-6">
                <label className="form-label">UTM medium</label>
                <input name="utmMedium" value={form.utmMedium} onChange={handleChange} className="form-control" />
              </div>
              <div className="col-md-6">
                <label className="form-label">Campaña</label>
                <input name="utmCampaign" value={form.utmCampaign} onChange={handleChange} className="form-control" />
              </div>
            </>
          )}
          <div className="col-12">
            <button type="submit" className="btn btn-primary" disabled={loading || Boolean(createdId)}>
              {loading ? 'Guardando...' : 'Guardar'}
            </button>
            <button type="button" className="btn btn-link ms-2" onClick={() => navigate(mode === 'edit' ? `/clientes/${id}` : '/clientes')}>
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ClientFormPage;
